import {Injectable} from '@angular/core';
import {Observable, of} from "rxjs";
import {emptyBrand, emptyChannel, emptyChannelProvider, emptyEvent, emptyEventChannel, emptyTemplate, emptyTemplateBody, emptyAttachment, IBrand, IChannel, IChannelProvider, IEvent, IEventChannel, ITemplate, ITemplateBody, IAttachment, ITemplateBodyParameter} from "@retail-data-factory/rdf-angular-types-api";
import {emptyServiceResponse, IServiceResponse} from "../../../../services/external-api/response-interfaces";
import {DataService} from "../../../../services/external-api/data.service";
import {map} from "rxjs/operators";
import {ConfigurationManager} from "../../../../store/configuration-manager/configuration-manager";

@Injectable({
  providedIn: 'root'
})
export class ApiNotificationService {

  constructor(
    private dataService: DataService,
    private configurationManager: ConfigurationManager
  ) {
  }

  /**
   * Получение списка брендов
   */
  getBrands(): Observable<Array<IBrand>> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/brands`, {}).pipe(
      map(result => {
        const brands: Array<IBrand> = result.report.map(item => this.mapBrandFromService(item));
        return brands;
      })
    );
  }

  /**
   * Получение конкретного бренда
   * @param brandCode Код бренда
   */
  getBrand(brandCode: string): Observable<IBrand> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/brands/${brandCode}`, {}).pipe(
      map(result => {
        if (result.report.length > 0) {
          return this.mapBrandFromService(result.report[0]);
        } else {
          return {...emptyBrand};
        }

      })
    );
  }

  /**
   * Создать бренд
   * @param brand Бренд
   */
  createBrand(brand: IBrand): Observable<IServiceResponse> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/brands`, {}, this.mapBrandToService(brand));
  }

  /**
   * Изменить бренд
   * @param brand Бренд
   */
  updateBrand(brand: IBrand): Observable<IServiceResponse> {
    return this.dataService.putData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/brands/${brand.brandCode}`, {}, this.mapBrandToService(brand));
  }

  /**
   * Удлаить бренд
   * @param brandCode код бренда
   */
  deleteBrand(brandCode): Observable<IServiceResponse> {
    return this.dataService.deleteData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/brands/${brandCode}`, {}, '');
  }

  /**
   * Получить каналы
   */
  getChannels(): Observable<Array<IChannel>> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/channels`, {}).pipe(
      map(result => {
        const channels: Array<IChannel> = result.report.map(item => this.mapChannelFromService(item));
        return channels;
      })
    );
  }

  /**
   * Получить канал по коду
   * @param channelCode Код канала
   */
  getChannel(channelCode: string): Observable<IChannel> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/channels/${channelCode}`, {}).pipe(
      map(result => {
        if (result.report.length > 0) {
          return this.mapChannelFromService(result.report[0]);
        } else {
          return {...emptyChannel};
        }

      })
    );
  }


  createChannel(channel: IChannel): Observable<IServiceResponse> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/channels`, {}, this.mapChannelToService(channel));
  }

  updateChannel(channel: IChannel): Observable<IServiceResponse> {
    return this.dataService.putData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/channels/${channel.channelCode}`, {}, this.mapChannelToService(channel));
  }

  deleteChannel(channelCode: string): Observable<IServiceResponse> {
    return this.dataService.deleteData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/channels/${channelCode}`, {}, '');
  }

  getChannelProviders(channelCode: string): Observable<Array<IChannelProvider>> {
    return of([]);
  }

  saveChannelProvider(channelProvider: IChannelProvider): Observable<IServiceResponse> {
    return of({...emptyServiceResponse});
  }

  deleteChannelProvider(channelProvider: IChannelProvider): Observable<IServiceResponse> {
    return of({...emptyServiceResponse});
  }

  getEvents(): Observable<Array<IEvent>> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/events`, {}).pipe(
      map(result => {
        const events: Array<IEvent> = result.report.map(item => this.mapEventFromService(item));
        return events;
      })
    );
  }

  getEvent(eventCode: string): Observable<IEvent> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/events/${eventCode}`, {}).pipe(
      map(result => {
        if (result.report.length > 0) {
          return this.mapEventFromService(result.report[0]);
        } else {
          return {...emptyEvent};
        }
      })
    );
  }

  createEvent(event: IEvent): Observable<IServiceResponse> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/events`, {}, this.mapEventToService(event));
  }

  updateEvent(event: IEvent): Observable<IServiceResponse> {
    return this.dataService.putData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/events/${event.eventCode}`, {}, this.mapEventToService(event));
  }

  deleteEvent(eventCode: string): Observable<IServiceResponse> {
    return this.dataService.deleteData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/events/${eventCode}`, {}, '');
  }

  getEventChannels(eventCode: string): Observable<Array<IEventChannel>> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/events/${eventCode}/channels`, {}).pipe(
      map(result => {
        const eventChannels: Array<IEventChannel> = result.report.map(item => this.mapEventChannelFromService(item));
        return eventChannels;
      })
    );
  }

  getEventChannel(eventCode: string, eventChannelCode: string): Observable<IEventChannel> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/events/${eventCode}/channels/${eventChannelCode}`, {}).pipe(
      map(result => {
        if (result.report.length > 0) {
          return this.mapEventChannelFromService(result.report[0]);
        } else {
          return {...emptyEventChannel};
        }
      })
    );
  }

  createEventChannel(eventChannel: IEventChannel): Observable<IServiceResponse> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/events/${eventChannel.eventCode}/channels`, {}, this.mapEventChannelToService(eventChannel));
  }

  updateEventChannel(eventChannel: IEventChannel): Observable<IServiceResponse> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/events/${eventChannel.eventCode}/channels/${eventChannel.channelCode}`, {}, this.mapEventChannelToService(eventChannel));
  }

  deleteEventChannel(eventChannel: IEventChannel): Observable<IServiceResponse> {
    return this.dataService.deleteData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/events/${eventChannel.eventCode}/channels/${eventChannel.channelCode}`, {}, '');
  }

  getTemplates(): Observable<Array<ITemplate>> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/templates`, {}).pipe(
      map(result => {
        const templates: Array<ITemplate> = result.report.map(item => this.mapTemplateFromService(item));
        return templates;
      })
    );
  }

  getTemplate(templateCode: string): Observable<ITemplate> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/templates/${templateCode}`, {}).pipe(
      map(result => {
        if (result.report.length > 0) {
          return this.mapTemplateFromService(result.report[0]);
        } else {
          return {...emptyTemplate};
        }
      })
    );
  }

  createTemplate(template: ITemplate): Observable<IServiceResponse> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/templates`, {}, this.mapTemplateToService(template));
  }

  updateTemplate(template: ITemplate): Observable<IServiceResponse> {
    return this.dataService.putData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/templates/${template.templateCode}`, {}, this.mapTemplateToService(template));
  }


  deleteTemplate(templateCode: string): Observable<IServiceResponse> {
    return this.dataService.deleteData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/templates/${templateCode}`, {}, '');
  }

  getTemplateBodyList(templateCode: string): Observable<Array<ITemplateBody>> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/templates/${templateCode}/bodies`, {}).pipe(
      map(result => {
        const templateBodyList: Array<ITemplateBody> = result.report.map(item => this.mapTemplateBodyFromService(item));
        return templateBodyList;
      })
    );
  }

  getTemplateBodyOld(templateCode: string, templateBodyCode: string): Observable<ITemplateBody> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/templates/${templateCode}/bodies/${templateBodyCode}`, {}).pipe(
      map(result => {
        if (result.report.length > 0) {
          return this.mapTemplateBodyFromService(result.report[0]);
        } else {
          return {...emptyTemplateBody};
        }
      })
    );
  }

  getTemplateBody(templateBodyCode: string): Observable<ITemplateBody> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/templates/bodies/${templateBodyCode}`, {}).pipe(
      map(result => {
        if (result.report.length > 0) {
          return this.mapTemplateBodyFromService(result.report[0]);
        } else {
          return {...emptyTemplateBody};
        }
      })
    );
  }

  createTemplateBody(templateBody: ITemplateBody): Observable<IServiceResponse> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/templates/${templateBody.templateCode}/bodies`, {}, this.mapTemplateBodyToService(templateBody));
  }

  updateTemplateBody(templateBody: ITemplateBody): Observable<IServiceResponse> {
    return this.dataService.putData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/templates/${templateBody.templateCode}/bodies/${templateBody.templateBodyCode}`, {}, this.mapTemplateBodyToService(templateBody));
  }

  deleteTemplateBody(templateBody: ITemplateBody): Observable<IServiceResponse> {
    return this.dataService.deleteData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/templates/${templateBody.templateCode}/bodies/${templateBody.templateBodyCode}`, {});
  }


  getTemplateBodyParameters(templateBodyId: string): Observable<Array<ITemplateBodyParameter>> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/templates/bodies/${templateBodyId}/parameters`, {}).pipe(
      map(result => {
        const templateBodyList: Array<ITemplateBodyParameter> = result.report.map(item => ({
          id: item['template_body_param_id'].toString(),
          name: item['param_name'] ? item['param_name'] : '',
          value: item['param_value'] ? item['param_value'] : ''
        }));
        return templateBodyList;
      })
    );
  }

  createTemplateBodyParameter(templateBodyCode: string, param: ITemplateBodyParameter): Observable<IServiceResponse> {
    const body = {
      "parameters": [
        {
          "param_name": param.name,
          "param_value": param.value
        }
      ]
    };
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/templates/bodies/${templateBodyCode}/parameters`, {}, body);
  }

  updateTemplateBodyParameter(templateBodyCode: string, param: ITemplateBodyParameter): Observable<IServiceResponse> {
    const body = {
      "parameters": [
        {
          "param_name": param.name,
          "param_value": param.value
        }
      ]
    };

    return this.dataService.putData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/templates/bodies/${templateBodyCode}/parameters/${param.id}`, {}, body);
  }

  deleteTemplateBodyParameter(templateBodyCode: string, param: ITemplateBodyParameter): Observable<IServiceResponse> {
    return this.dataService.deleteData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/templates/bodies/${templateBodyCode}/parameters/${param.id}`, {});
  }


  getTemplateBodyAttachments(templateBodyCode: string, withFiles = false): Observable<Array<IAttachment>> {
    const header = {
      files: withFiles
    };
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/templates/bodies/${templateBodyCode}/attachments${withFiles ? '?with_body=true' : ''}`, header).pipe(
      map(result => {
        const templateBodyAttachments: Array<IAttachment> = result.report.map(item => ({
          ...emptyAttachment,
          id: item['template_body_attachment_id'].toString(),
          filename: item['file_name'] ? item['file_name'] : '',
          type: item['file_type'] ? item['file_type'] : '',
          body: item['file_body'] ? item['file_body'] : '',
          size: item['profile'] && item['profile'].size ? item['profile'].size : 0
        }));
        return templateBodyAttachments;
      })
    );
  }

  getTemplateBodyAttachment(templateBodyCode: string, attachmentId: string): Observable<IAttachment> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/templates/bodies/${templateBodyCode}/attachments`, {}).pipe(
      map(result => {
        if (result.report.length) {

          const item = result.report[0];
          const templateBodyAttachment: IAttachment = {
            ...emptyAttachment,
            id: item['template_body_param_id'].toString(),
            filename: item['param_name'] ? item['param_name'] : '',
            type: item['param_value'] ? item['param_value'] : '',
            body: item['param_value'] ? item['param_value'] : ''
          };
          return templateBodyAttachment;
        } else {
          return {...emptyAttachment};
        }
      })
    );
  }

  createTemplateBodyAttachment(templateBodyCode: string, attachment: IAttachment): Observable<IServiceResponse> {
    const body = {
      "file_type": attachment.type,
      "file_name": attachment.filename,
      "file_body": attachment.body,
      "profile": {
        size: attachment.size
      }
    };
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/templates/bodies/${templateBodyCode}/attachments`, {}, body);
  }

  deleteTemplateBodyAttachment(templateBodyCode: string, attachmentId: string): Observable<IServiceResponse> {
    return this.dataService.deleteData(`${this.configurationManager.getApiUrl('apiNotificationUrl')}/templates/bodies/${templateBodyCode}/attachments/${attachmentId}`, {});
  }


  sendEmail(recipients: string, subject: string, fileBody: string, attachments: Array<IAttachment>): Observable<IServiceResponse> {
    const body = {
      "subject": subject,
      "message": fileBody,
      "mailTo": recipients,
      "files": attachments
        .filter(t => !!t.body)
        .map(t => ({
          file_name: t.filename,
          file: t.body
        }))
    };
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiSendUrl')}/gate/mail`, {}, body);
  }


  private mapBrandFromService(item: object): IBrand {
    return {
      ...emptyBrand,
      brandCode: item['brand_code'],
      brandName: item['brand_name'],
      brandDescription: item['brand_description']
    };
  }

  private mapBrandToService(brand: IBrand): object {
    return {
      brand_code: brand.brandCode,
      brand_name: brand.brandName,
      brand_description: brand.brandDescription
    };
  }

  private mapChannelFromService(item: object): IChannel {
    return {
      ...emptyChannel,
      channelCode: item['channel_code'],
      channelName: item['channel_name'],
      channelDescription: item['channel_description'],
      profile: item['profile'],
    };
  }

  private mapChannelToService(channel: IChannel): object {
    return {
      channel_code: channel.channelCode,
      channel_name: channel.channelName,
      channel_description: channel.channelDescription,
      profile: {}

    };
  }

  private mapChannelProviderFromService(item: object) {
    return {
      ...emptyChannelProvider,
      providerCode: item['channel_provider_code'],
      providerName: item['channel_provider_name'],
      providerDescription: item['channel_provider_description'],
      providerLogin: item['channel_provider_login'],
      channelCode: item['channel_code'],
      channelName: item[''],
      brandCode: item['brand_code'],
      brandName: '',
      statusCode: item['channel_provider_status'],
      profile: item['profile']
    };
  }

  private mapChannelProviderToService(channelProvider: IChannelProvider): object {
    return {
      channel_code: channelProvider.channelCode,
      brand_code: channelProvider.brandCode,
      channel_provider_code: channelProvider.providerCode,
      channel_provider_login: channelProvider.providerLogin,
      channel_provider_status: channelProvider.statusCode,
      channel_provider_name: channelProvider.providerName,
      channel_provider_description: channelProvider.providerDescription,
      profile: {}

    };
  }

  private mapEventFromService(item: object): IEvent {
    return {
      ...emptyEvent,
      eventCode: item['event_code'],
      eventName: item['event_name'],
      eventDescription: item['event_description'],
      templateCode: item['template_code'],
      businessRuleCode: item['profile']['rule_code'] ? item['profile']['rule_code'] : '',
      profile: item['profile'],
    };
  }

  private mapEventToService(event: IEvent): object {
    return {
      event_code: event.eventCode,
      template_code: event.templateCode,
      event_name: event.eventName,
      event_description: event.eventDescription,
      profile: {
        rule_code: event.businessRuleCode ? event.businessRuleCode : ''
      }
    };
  }

  private mapEventChannelFromService(item: object): IEventChannel {
    return {
      ...emptyEventChannel,
      eventChannelCode: item['event_channel_code'],
      eventCode: item['event_code'],
      channelCode: item['channel_code'],
      statusCode: item['event_channel_status'],
    };
  }

  private mapEventChannelToService(eventChannel: IEventChannel): object {
    return {
      event_channel_code: eventChannel.eventChannelCode,
      event_code: eventChannel.eventCode,
      channel_code: eventChannel.channelCode,
      event_channel_status: eventChannel.statusCode

    };
  }

  private mapTemplateFromService(item: object): ITemplate {
    return {
      ...emptyTemplate,
      templateCode: item['template_code'],
      templateName: item['template_name'],
      templateDescription: item['template_description'],
      templateStatusCode: item['template_status'],
      profile: {}
    };
  }

  private mapTemplateToService(template: ITemplate): object {
    return {
      template_code: template.templateCode,
      template_name: template.templateName,
      template_description: template.templateDescription,
      template_status: template.templateStatusCode,
    };
  }

  private mapTemplateBodyFromService(item: object): ITemplateBody {
    return {
      ...emptyTemplateBody,
      templateBodyCode: item['template_body_code'].toString(),
      templateCode: item['template_code'],
      brandCode: item['brand_code'],
      channelCode: item['channel_code'],
      statusCode: item['template_body_status'],
      langCode: item['language_code'],
      subject: item['profile']['subject'] ? item['profile']['subject'] : '',
      body: item['template_body'],
      profile: item['profile']
    };
  }

  private mapTemplateBodyToService(templateBody: ITemplateBody): object {
    return {
      template_body_code: templateBody.templateBodyCode,
      template_code: templateBody.templateCode,
      channel_code: templateBody.channelCode,
      brand_code: templateBody.brandCode,
      template_body_status: templateBody.statusCode,
      template_body: templateBody.body,
      language_code: templateBody.langCode,
      profile: {
        subject: templateBody.subject ? templateBody.subject : ''
      }
    };
  }
}
